import { useAuth } from '../context/AuthContext';
import { useCallback, useMemo } from 'react';

/**
 * Map of feature keys to the routes/buttons they protect.
 * Feature keys come from the plan's `features` array in the DB.
 */
const FEATURE_ROUTE_MAP = {
  '/daily': 'daily_tracker',
  '/menu': 'menu',
  '/food-cost': 'menu',
  '/operations': 'daily_tracker',
  '/pnl': 'pnl',
  '/cash-flow': 'cash_flow',
  '/cash-flow-simulator': 'scenario_simulator',
  '/expense-analysis': 'expense_alerts',
  '/viability': 'stress_test',
  '/benchmark': 'benchmark',
  '/menu-engineering': 'menu_engineering',
  '/competitors': 'competitors',
  '/staff': 'staff',
  '/staff-analysis': 'staff_optimizer',
  '/expenses': 'expenses',
  '/reports': 'month_comparison',
  '/integrations': null, // always available
  '/ai-insights': 'ai_insights',
};

/**
 * Feature labels for the upgrade dialog (IT/EN)
 */
const FEATURE_LABELS = {
  pnl: { it: 'Conto Economico (P&L)', en: 'Profit & Loss (P&L)' },
  cash_flow: { it: 'Previsione Cassa', en: 'Cash Flow' },
  scenario_simulator: { it: 'Simulatore Cash Flow', en: 'Cash Flow Simulator' },
  expense_alerts: { it: 'Analisi Spese', en: 'Expense Analysis' },
  stress_test: { it: 'Stress Test', en: 'Stress Test' },
  benchmark: { it: 'Benchmark', en: 'Benchmark' },
  menu_engineering: { it: 'Menu Engineering', en: 'Menu Engineering' },
  competitors: { it: 'Competitor', en: 'Competitors' },
  staff_optimizer: { it: 'Analisi Personale', en: 'Staff Analysis' },
  ai_insights: { it: 'AI Insights (Sofia)', en: 'AI Insights (Sofia)' },
  invoice_scanner: { it: 'Scanner Fatture AI', en: 'AI Invoice Scanner' },
  pdf_reports: { it: 'Report PDF', en: 'PDF Reports' },
};

export function useFeatureGating() {
  const { user } = useAuth();

  const features = useMemo(() => {
    return user?.plan_features || [];
  }, [user?.plan_features]);

  const isAdmin = useMemo(() => {
    return user?.role === 'superadmin' || user?.role === 'admin';
  }, [user?.role]);

  const hasFeature = useCallback((featureKey) => {
    if (isAdmin) return true;
    if (!featureKey) return true;
    return features.includes(featureKey);
  }, [features, isAdmin]);

  const getFeatureForRoute = useCallback((routeSuffix) => {
    for (const [key, feature] of Object.entries(FEATURE_ROUTE_MAP)) {
      if (routeSuffix.includes(key)) return feature;
    }
    return null;
  }, []);

  const canAccessRoute = useCallback((routeSuffix) => {
    const feature = getFeatureForRoute(routeSuffix);
    return hasFeature(feature);
  }, [getFeatureForRoute, hasFeature]);

  return {
    features,
    hasFeature,
    canAccessRoute,
    isAdmin,
    userPlan: user?.plan || 'base',
    FEATURE_LABELS,
  };
}
